const MIN_LENGTH = 2
const DEBOUNCE = 250
const RECENT_KEY = 'ttm-search-recent'
const RECENT_LIMIT = 5
const ITEM_SELECTOR = 'a[href], [data-search-item]'

interface SearchResponse {
    html: string
    url: string
    total: number
}

function loadRecent(): string[] {
    try {
        const stored = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]')

        return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string').slice(0, RECENT_LIMIT) : []
    } catch {
        return []
    }
}

function isEditable(target: EventTarget | null): boolean {
    return target instanceof HTMLElement
        && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
}

/**
 * Dlouhý placeholder pole hledání se na mobilu nevejde vedle tlačítka – vymění se za krátký.
 */
export function shortenPlaceholders(): void {
    const narrow = window.matchMedia('(max-width: 47.99rem)')

    const apply = (): void => document.querySelectorAll<HTMLInputElement>('[data-placeholder-short]').forEach((input) => {
        input.dataset.placeholderLong ??= input.placeholder
        input.placeholder = (narrow.matches ? input.dataset.placeholderShort : input.dataset.placeholderLong) ?? ''
    })

    apply()
    narrow.addEventListener('change', apply)
}

/**
 * Panel vyhledávání v hlavičce (`partials/search/overlay`). Výsledky vykresluje
 * server (REST `ttm/v1/search`), komponenta drží jen dotaz a sekci.
 */
export default (endpoint: string, sections: Record<string, string>) => ({
    query: '',
    section: '',
    html: '',
    url: '',
    status: '',
    loading: false,
    recent: [] as string[],
    timer: 0,
    controller: null as AbortController | null,

    init(): void {
        this.recent = loadRecent()
        this.$watch('query', () => this.schedule())

        document.addEventListener('click', (event) => {
            const trigger = (event.target as HTMLElement).closest<HTMLElement>('[data-search-open]')
            if (!trigger || event.defaultPrevented || event.metaKey || event.ctrlKey) return

            event.preventDefault()
            this.open(trigger.dataset.searchOpen ?? '')
        })

        document.addEventListener('keydown', (event) => {
            const shortcut = (event.key === 'k' && (event.metaKey || event.ctrlKey))
                || (event.key === '/' && !event.metaKey && !event.ctrlKey && !isEditable(event.target))

            if (shortcut && !this.dialog().open) {
                event.preventDefault()
                this.open()
            }
        })
    },

    get active(): boolean {
        return this.query.trim().length >= MIN_LENGTH || this.section !== ''
    },

    dialog(): HTMLDialogElement {
        return this.$root as HTMLDialogElement
    },

    open(section = ''): void {
        if (section in sections && section !== this.section) {
            this.setSection(section)
        }

        this.dialog().showModal()
        document.body.classList.add('u-hiddenOverflow')
        this.$refs.input.select()
    },

    close(): void {
        this.dialog().close()
    },

    onClose(): void {
        document.body.classList.remove('u-hiddenOverflow')
    },

    /**
     * Sekce se přepíná i odkazem hluboko ve výsledcích – panel se vrátí nahoru
     * a zvolená záložka se posune do viditelné části posuvného pruhu.
     */
    setSection(section: string): void {
        this.section = section
        this.fetch()
        this.$refs.input.focus()
        this.dialog().scrollTop = 0
        this.$nextTick(() => {
            const tabs = this.$refs.tabs as HTMLElement
            const tab = tabs.querySelector<HTMLElement>('.is-active')
            if (tab) tabs.scrollLeft += tab.getBoundingClientRect().left - tabs.getBoundingClientRect().left - (tabs.clientWidth - tab.offsetWidth) / 2
        })
    },

    schedule(): void {
        window.clearTimeout(this.timer)
        this.timer = window.setTimeout(() => this.fetch(), DEBOUNCE)
    },

    async fetch(): Promise<void> {
        window.clearTimeout(this.timer)
        this.controller?.abort()

        if (!this.active) {
            this.html = ''
            this.status = ''
            this.loading = false
            return
        }

        const query = this.query.trim()
        const params = new URLSearchParams()

        if (query.length >= MIN_LENGTH) params.set('hledat', query)
        if (this.section) params.set('sekce', this.section)

        const controller = new AbortController()
        this.controller = controller
        this.loading = true

        try {
            const response = await fetch(`${endpoint}?${params}`, {
                signal: controller.signal,
                headers: { Accept: 'application/json' },
            })

            if (!response.ok) throw new Error(String(response.status))

            const data = await response.json() as SearchResponse

            this.html = data.html
            this.url = data.url
            this.status = data.total ? `Nalezeno výsledků: ${data.total}` : 'Nic nenalezeno'
        } catch (error) {
            if (controller.signal.aborted) return

            this.status = 'Vyhledávání se nepodařilo načíst. Zkuste to prosím znovu.'
            this.html = `<p class="SearchZero-hint">${this.status}</p>`
        } finally {
            if (this.controller === controller) this.loading = false
        }
    },

    onBodyClick(event: MouseEvent): void {
        const target = event.target as HTMLElement
        const sectionLink = target.closest<HTMLElement>('[data-search-section]')

        if (sectionLink) {
            event.preventDefault()
            this.setSection(sectionLink.dataset.searchSection ?? '')
            return
        }

        if (target.closest('a[href]')) this.remember()
    },

    items(): HTMLElement[] {
        return Array.from((this.$refs.body as HTMLElement).querySelectorAll<HTMLElement>(ITEM_SELECTOR))
            .filter((item) => item.offsetParent !== null)
    },

    /**
     * Šipky procházejí input a položky panelu skutečným fokusem – čtečka tak
     * čte odkazy tak, jak jsou, bez zprostředkování přes aria-activedescendant.
     */
    onKeydown(event: KeyboardEvent): void {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
        const items = this.items()
        const index = event.target === this.$refs.input ? -1 : items.indexOf(event.target as HTMLElement)

        if (index === -1 && event.target !== this.$refs.input) return

        event.preventDefault()

        const next = index + (event.key === 'ArrowDown' ? 1 : -1)

        if (next < 0) {
            this.$refs.input.focus()
        } else {
            items[Math.min(next, items.length - 1)]?.focus()
        }
    },

    remember(): void {
        const term = this.query.trim()
        if (term.length < MIN_LENGTH) return

        this.recent = [term, ...this.recent.filter((item) => item.toLowerCase() !== term.toLowerCase())]
            .slice(0, RECENT_LIMIT)

        try {
            window.localStorage.setItem(RECENT_KEY, JSON.stringify(this.recent))
        } catch {
            // Úložiště může být zakázané (soukromé okno), historie pak jen nepřežije načtení.
        }
    },

    useRecent(term: string): void {
        this.query = term
        this.$refs.input.focus()
    },
})
